import type { AudioResultSimpleJson } from '@azure/openai'

// utils
import API, { QueryParams } from '@/utils/api/api'
import Utils from '@/utils/utils'
import getBackendLanguageCode from '@/utils/language'
import { constants } from './constants'
import { i18n } from '@/utils/i18n'
import type {
    AdapterParams,
    AggregatedRawIncomingMessage,
    HistoryResponse,
    MetadataResponse,
} from '@/utils/ai-adapters/xdc/types'

class XdcInstance {
    #endpoint
    #customMappings
    #mode: unknown
    name: string

    constructor(endpoint: string | undefined, mappings: Record<string, string>) {
        this.name = 'XdcInstance'
        this.#endpoint = endpoint
        this.#customMappings = mappings
    }

    async getAssistantHistory(page: number, params: AdapterParams) {
        const suffix = this.#customMappings?.['assistant-history'] || constants.mapping['assistant-history']

        const endpoint = `${this.#endpoint}${Utils.formatEndpoint(suffix, params)}`
        const query = {
            userID: params.userId,
            chatID: params.chatID,
            page: page,
        }

        return await API.get<HistoryResponse>(endpoint, query, this.getAuthorizationHeader(params.token))
    }

    async getChatHistory(page: number, params: AdapterParams) {
        const suffix = this.#customMappings?.['chat-history'] || constants.mapping['chat-history']
        const endpoint = `${this.#endpoint}${Utils.formatEndpoint(suffix, params)}`
        const query = {
            userID: params.userId,
        }

        return await API.get<HistoryResponse>(endpoint, query, this.getAuthorizationHeader(params.token))
    }

    /**
     * TODO: not tested so far! Changes based on streamChatCompleations() method
     *
     * @param {*} content
     * @param {*} userId
     * @param {*} chatId
     * @returns
     */
    async getChatCompletions(content: string, params: AdapterParams) {
        console.warn('getChatCompletions is not implemented yet')

        const suffix =
            this.#customMappings?.['chat-completion'] ||
            constants.mapping['chat-completion']
        const url = `${this.#endpoint}${suffix}`

        const query = {
            userID: params.userId,
            chatID: params.chatId,
        }

        const rawResponse = await API.post<AggregatedRawIncomingMessage>(
            url,
            query,
            content,
            this.getAuthorizationHeader(params.token)
        )

        return rawResponse
    }

    async streamChatCompletions(content: string, params: AdapterParams) {
        const hasCustomEndpoint = this.#customMappings?.['chat-completion']
        const defaultEndpoint = constants.mapping['chat-completion']
        params.languageCode = getBackendLanguageCode(i18n.language, params.additionalBackendSupportedLanguages)

        const suffix = hasCustomEndpoint || defaultEndpoint
        const endpoint = `${this.#endpoint}${Utils.formatEndpoint(suffix, params)}`

        const query = {
            userID: params.userId,
            chatID: params.chatId,
            languageCode: params.languageCode,
            searchDepth: params.searchDepth || 0,
        }

        return await API.postSSE(
            endpoint,
            query,
            content,
            this.getAuthorizationHeader(params.token)
        )
    }

    async getMetadata(params: AdapterParams) {
        const hasCustomEndpoint = this.#customMappings?.['metadata']
        const defaultEndpoint = constants.mapping['metadata']
        const suffix = hasCustomEndpoint || defaultEndpoint

        const endpoint = `${this.#endpoint}${Utils.formatEndpoint(suffix, params)}`
        const query = {
            assistant: params.assistantEndpoint,
            object: params.object,
        }

        return await API.get<MetadataResponse>(endpoint, query, this.getAuthorizationHeader(params.token))
    }

    async getAudioTranscription(audio: Blob, params: AdapterParams) {
        const hasCustomEndpoint = this.#customMappings?.['speech-to-text']
        const defaultEndpoint = constants.mapping['speech-to-text']
        const suffix = hasCustomEndpoint || defaultEndpoint

        const endpoint = `${this.#endpoint}${Utils.formatEndpoint(suffix, params)}`
        const query = {
            userID: params.userId,
            languageCode: params.languageCode,
        }

        const formData = new FormData()
        formData.append('audio', audio)

        const rawResponse = await API.post<AudioResultSimpleJson>(
            endpoint,
            query,
            formData,
            this.getAuthorizationHeader(params.token)
        )

        return rawResponse
    }

    async sendCustomCall(url: string, options: RequestInit, params: AdapterParams, query?: QueryParams) {
        console.log('sendCustomCall ', url, params?.userId)

        const defaultQuery = {
            userID: params.userId,
            chatID: params.chatId,
        }

        const extendedOptions = {
            ...options,
            headers: {
                ...this.getAuthorizationHeader(params.token),
            }
        }

        if (!query)
            query = defaultQuery
        else
            query = { ...defaultQuery, ...query }

        const endpoint = `${this.#endpoint}${Utils.formatEndpoint(url, params)}`

        return API.rawFetch(endpoint, extendedOptions, query)
    }

    async sendFeedback(userId: string, chatId: string, messageId: string, type: string, freeText: string, checkboxes: string[]) {
        const getFeedbackBody = (freeText: string, checkboxes: string[]) => {
            const checkboxesData = checkboxes.reduce(
                (acc, checkbox) => ({
                    ...acc,
                    [checkbox]: true,
                }),
                {}
            )

            return JSON.stringify({
                [type]: {
                    freeText: freeText,
                    checkBoxes: checkboxesData,
                },
            })
        }

        const headers = {
            'Content-Type': 'application/json',
            // ...this.getAuthorizationHeader(token),
        }
        const url = `${this.#endpoint}/assistant/${this.#mode}/chat/${chatId}/feedback/${messageId}`
        const query = {
            userID: userId,
        }

        const body = getFeedbackBody(freeText, checkboxes)

        const rawResponse = await API.post(url, query, body, headers)
        return rawResponse
    }

    getAuthorizationHeader(token: string | undefined) {
        return {
            Authorization: `Bearer ${token}`,
        }
    }
}

export default XdcInstance
